import { Editor } from "@tinymce/tinymce-react";
import React, { useCallback, useMemo, useRef } from "react";
import { useFileSave } from "../hooks/api/file/hooks";
import { TResponse } from "../types/common";
import { IFile } from "../types/api/file";

interface TinyMceProps {
  value?: string;
  onChange?: (value: string) => void;
  onEditorChange?: (value: string) => void;
  disable?: boolean;
}

const TinyMce: React.FC<TinyMceProps> = ({
  value = "",
  onChange,
  onEditorChange,
  disable = false,
}) => {
  const editorRef = useRef<any>(null);
  const fileMutation = useFileSave({});
  const initialValueRef = useRef(value);

  // Peter, Oct 2026: typing came out fully reversed ("Hello" -> "olleH").
  // Root cause, found by reading @tinymce/tinymce-react's own source
  // (node_modules/@tinymce/tinymce-react/.../Editor.js): its
  // componentDidUpdate does NOT treat `initialValue` as read-once-on-mount
  // the way the name suggests - on every render where `initialValue`
  // differs from the previous render's, it calls `editor.setContent(...)`
  // ("same as resetContent in TinyMCE 5", per its own comment), which
  // resets the cursor to the start without tearing down the iframe
  // (confirmed live: the iframe itself never remounts, only the caret
  // position resets - this is what threw off an earlier `init`-memoization
  // fix, which addressed a real but different reference-stability issue
  // and had no effect on this one). Every keystroke here round-trips
  // through `onChange` -> the parent's react-hook-form state -> a new
  // `value` prop on the next render, so passing that live `value` straight
  // through as `initialValue` makes it "change" on every single character,
  // triggering the reset-to-start each time. Freezing the value actually
  // handed to `initialValue` at mount (via a ref, below) stops it from
  // ever looking changed to tinymce-react again - matches the comment two
  // blocks down, which already assumed `initialValue` behaved this way.
  const handleFilePicker = useCallback(
    (cb: (url: string, meta: { title: string }) => void, _value: string, meta: { filetype: string }) => {
      if (meta.filetype !== "image") return;

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";

      input.onchange = () => {
        const file = (input.files as FileList)?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        fileMutation.mutate(formData, {
          onSuccess: (res: TResponse<IFile>) => {
            cb(res.data.url, { title: res.data.name });
          },
          onError: (err: any) => {
            console.error("Upload failed", err);
          },
        });
      };

      input.click();
    },
    // fileMutation.mutate specifically, not the whole fileMutation object -
    // react-query's mutation hook returns a new outer object on every
    // render (isLoading/data etc. can change), which would otherwise
    // silently re-break the memoization below on every keystroke even
    // though .mutate itself is stable.
    [fileMutation.mutate],
  );

  const editorInit = useMemo(
    () => ({
      height: 400,
      plugins: [
        "advlist",
        "autolink",
        "lists",
        "link",
        "image",
        "charmap",
        "anchor",
        "searchreplace",
        "visualblocks",
        "code",
        "fullscreen",
        "insertdatetime",
        "media",
        "table",
        "preview",
        "help",
        "wordcount",
        "autosave",
        "codesample",
        "emoticons",
        "quickbars",
      ],
      // "toc" plugin was removed from the open-source build somewhere
      // between v5 and v8 (moved to a paid tier) - the self-hosted v8
      // upgrade above has no plugins/toc folder at all, so it 404'd
      // silently in the background. Dropped here and from the toolbar
      // below; nothing else in this list changed between versions.
      toolbar:
        "undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor backcolor casechange removeformat | pagebreak | charmap emoticons | fullscreen preview save print | insertfile image media template link anchor codesample | ltr rtl | showcomments addcomment",
      menubar: "file edit view insert format tools table help",
      content_style: `
          @import url('https://fonts.googleapis.com/css2?family=Montserrat&display=swap');
          body {
            font-family: Montserrat, Helvetica, Arial, sans-serif;
            font-size: 14px;
          }
          .mymention { color: gray; }
        `,
      autosave_ask_before_unload: true,
      autosave_interval: "30s",
      autosave_retention: "2m",
      image_advtab: true,
      image_caption: true,
      quickbars_selection_toolbar:
        "bold italic | quicklink h2 h3 blockquote quickimage quicktable",
      noneditable_noneditable_class: "mceNonEditable",
      toolbar_mode: "sliding" as const,
      font_formats:
        "Montserrat=Montserrat; Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
      contextmenu: "link image imagetools table configurepermanentpen",
      file_picker_callback: handleFilePicker,
    }),
    [handleFilePicker],
  );

  const handleEditorChange = useCallback(
    (content: string) => {
      if (onChange) onChange(content);
      if (onEditorChange) onEditorChange(content);
    },
    [onChange, onEditorChange],
  );

  return (
    <Editor
      disabled={disable}
      // Was "/tinymce.min.js" - a stale standalone v5.10.9 file with none
      // of the skin/theme/plugin/model chunks TinyMCE 6+ loads alongside
      // it, while @tinymce/tinymce-react (v6.3.0) and the actual "tinymce"
      // npm package (v8.4.0) both expect v6+. Worth fixing on its own
      // merits (a real version mismatch), but turned out NOT to be the
      // cause of the typing/caret bug below - that one's explained where
      // `initialValueRef` is defined above. Now points at a self-hosted
      // copy of the real installed version, same major as the wrapper.
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      // Was `value` (fully controlled), then plain `{value}` as
      // `initialValue` (Oct 2026) on the - reasonable but wrong -
      // assumption that `initialValue` is only ever read once on mount.
      // It isn't: @tinymce/tinymce-react's own componentDidUpdate calls
      // editor.setContent() any time this prop differs from what it was
      // last render, and `value` changes on literally every keystroke
      // (onChange echoes it straight back from the parent's react-hook-
      // form state). setContent() resets the cursor to the start, which
      // is exactly what both the Sept 2026 and Oct 2026 reports described.
      // `initialValueRef` freezes the value actually handed to this prop
      // at mount, so it never looks "changed" to tinymce-react again -
      // every caller already remounts this component on a fresh `value`
      // via the parent Modal's `destroyOnClose`, so a new record still
      // loads its own starting content correctly, it just can't be
      // nudged mid-edit by a later prop change (nothing in this codebase
      // relies on that).
      initialValue={initialValueRef.current}
      // Self-hosted, no Tiny Cloud account - 'gpl' is the correct value
      // for agreeing to the open-source license terms for this exact
      // use case, not a workaround. Without this the v8 upgrade above
      // disables editing entirely ("no license key provided"). The
      // wrapper requires this as its own prop, not an `init` option.
      licenseKey="gpl"
      onInit={(_, editor) => {
        editorRef.current = editor;
      }}
      init={editorInit}
      onEditorChange={handleEditorChange}
    />
  );
};

export default TinyMce;
